import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useRange, withRange } from '../../range';
import { Band, Card, Field } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import LineTrend from '../../components/LineTrend';
import PageHeader from '../../components/PageHeader';
import { ReasonDialog } from '../../components/Dialog';
import { formatCompactNumber, formatDateTime, formatPct, formatRelative } from '../../format';

/**
 * One product.
 *
 * A SKU previously had no page at all — it was a truncated title in a
 * twelve-column table and a search result, with its moderation control
 * squeezed into a table cell. The question this page exists to answer is
 * "why is this item not showing, and who did that", which used to require
 * reading three different places.
 *
 * The visibility panel is the reason it earns a page. A product can be
 * invisible for three independent reasons — the crawler delisted it, every
 * size sold out at the source, or an operator hid it — and they call for
 * completely different responses. Collapsing them into one "visible" boolean
 * loses exactly the information someone came here for.
 */
export default function ProductDetailPage() {
  const { sku } = useParams();
  const range = useRange();
  const { admin } = useAdminAuth();
  const canModerate = hasRole(admin, 'operator', 'owner');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moderating, setModerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.get(`/product/${encodeURIComponent(sku)}`, {
        params: { days: range.days },
      });
      setData(data);
    } catch (err) {
      setError(errMsg(err, 'Could not load this product'));
    } finally {
      setLoading(false);
    }
  }, [sku, range.days]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (reason) => {
    const hidden = !data.hidden;
    try {
      await adminApi.patch(`/ops/products/${encodeURIComponent(sku)}`, { hidden, reason });
      toast.success(hidden ? 'Product hidden' : 'Product re-enabled');
      setModerating(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update product'));
      setModerating(false);
    }
  };

  if (loading && !data) return <Card><p className="text-sm text-ink-muted">Loading…</p></Card>;
  if (error) {
    return (
      <div>
        <PageHeader title="Product" back={{ to: withRange('/catalog/products', range), label: 'All products' }} />
        <EmptyState title="Could not load this product" detail={error} />
      </div>
    );
  }
  if (!data) return null;

  const eng = data.engagement || {};
  const daily = data.daily || [];
  const image = (data.image_urls || [])[0];

  // Three signals, three axes of meaning — an item can be delisted AND
  // hidden, and the fix differs.
  const visibility = [
    {
      key: 'crawler',
      label: 'Crawler',
      ok: data.is_active !== false,
      good: 'Still listed at the source',
      bad: 'Delisted — the crawler no longer finds it',
    },
    {
      key: 'stock',
      label: 'Availability',
      ok: data.platform_available !== false,
      good: 'At least one size in stock',
      bad: 'Every size sold out at the source',
    },
    {
      key: 'moderation',
      label: 'Moderation',
      ok: !data.hidden,
      good: 'Not hidden by anyone',
      bad: 'Hidden by an operator',
    },
  ];
  const invisible = visibility.some((v) => !v.ok);

  const trendSeries = [
    { key: 'impressions', label: 'Impressions', color: '#2a78d6', values: daily.map((d) => ({ x: d.date, y: d.impressions })) },
    { key: 'saves', label: 'Saved', color: '#1baf7a', values: daily.map((d) => ({ x: d.date, y: d.saves })) },
    { key: 'passes', label: 'Passed', color: '#eb6834', values: daily.map((d) => ({ x: d.date, y: d.passes })) },
  ];

  return (
    <div>
      <PageHeader
        title={data.title || data.mira_sku}
        back={{ to: withRange('/catalog/products', range), label: 'All products' }}
        hint={
          <>
            <Link to={withRange(`/brand/${encodeURIComponent(data.brand_id)}`, range)} className="font-medium text-raspberry-600 hover:text-raspberry-700">
              {data.brand_name}
            </Link>
            {data.category ? ` · ${data.category}` : ''}
          </>
        }
        actions={canModerate && (
          <button
            onClick={() => setModerating(true)}
            className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
              data.hidden
                ? 'border-status-good/30 text-status-good hover:bg-status-good/10'
                : 'border-status-critical/30 text-status-critical hover:bg-status-critical/10'
            }`}
          >
            {data.hidden ? 'Re-enable' : 'Hide from discovery'}
          </button>
        )}
      />

      <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Visibility" className={invisible ? 'border-status-warning/40' : undefined}>
          <ul className="space-y-2.5">
            {visibility.map((v) => (
              <li key={v.key} className="flex items-start gap-2.5">
                <span
                  className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${v.ok ? 'bg-status-good' : 'bg-status-critical'}`}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-ink">{v.label}</span>
                  <span className="block text-[11px] leading-snug text-ink-secondary">{v.ok ? v.good : v.bad}</span>
                </span>
              </li>
            ))}
          </ul>
          {data.hidden && (
            <div className="mt-3 rounded-lg bg-status-critical/[0.05] px-3 py-2 text-[11px] leading-relaxed text-ink-secondary">
              <span className="font-semibold text-status-critical">Hidden</span>
              {data.hidden_by && <> by {data.hidden_by}</>}
              {data.hidden_at && <> · {formatRelative(data.hidden_at)}</>}
              {data.hidden_reason_note && <div className="mt-1 italic">&ldquo;{data.hidden_reason_note}&rdquo;</div>}
            </div>
          )}
          {invisible && (
            <p className="mt-3 text-[11px] leading-relaxed text-ink-muted">
              Anyone who already saved this keeps seeing it in their Wardrobe — they chose to save it, so it
              is not removed from under them.
            </p>
          )}
        </Card>

        <Card title="Catalogue" className="lg:col-span-2">
          <div className="flex gap-4">
            {image && (
              <img
                src={image}
                alt=""
                className="h-32 w-24 flex-shrink-0 rounded-lg border border-hairline object-cover"
                loading="lazy"
              />
            )}
            <dl className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3">
              <Field label="SKU" mono>{data.mira_sku}</Field>
              <Field label="Price">
                {data.price != null ? `${data.currency || '₹'}${data.price}` : null}
              </Field>
              <Field label="Sizes">{(data.sizes || []).join(', ') || null}</Field>
              <Field label="Source" mono>{data.source_id}</Field>
              <Field label="First seen">{formatDateTime(data.first_seen_at)}</Field>
              <Field label="Last crawled">
                {data.last_crawled_at ? formatRelative(data.last_crawled_at) : null}
              </Field>
              {data.product_url && (
                <Field label="At the brand">
                  <a
                    href={data.product_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-raspberry-600 hover:text-raspberry-700"
                  >
                    Open product page ↗
                  </a>
                </Field>
              )}
            </dl>
          </div>
        </Card>
      </div>

      <Band title={`Engagement — ${range.label.toLowerCase()}`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile label="Impressions" value={formatCompactNumber(eng.impressions)} />
          <KpiTile
            label="Saved"
            value={formatCompactNumber(eng.saves)}
            caption={`Save rate ${formatPct(eng.save_rate_pct, 1)}`}
          />
          <KpiTile label="Try-ons" value={formatCompactNumber(eng.tryons)}
                   caption={`Completed ${formatPct(eng.tryon_completion_rate_pct, 1)}`} />
          <KpiTile
            label="Billable clicks"
            value={formatCompactNumber(eng.billable_clicks)}
            caption="Deduplicated, bot-filtered. Not a purchase count."
          />
        </div>
      </Band>

      <Band title="Daily">
        {daily.every((d) => d.impressions === 0 && d.saves === 0 && d.passes === 0) ? (
          <EmptyState
            title="No activity in this window"
            detail="Nobody was shown this product on any day in range. That is different from it performing badly."
          />
        ) : (
          <LineTrend series={trendSeries} height={180} format={(v) => Math.round(v)} />
        )}
      </Band>

      <ReasonDialog
        open={moderating}
        tone={data.hidden ? 'default' : 'critical'}
        title={data.hidden ? 'Re-enable this product?' : 'Hide this product?'}
        body={data.hidden
          ? 'It becomes eligible to appear in discovery again, provided the crawler still lists it as available.'
          : 'It stops appearing in discovery, search and anything the stylist can recommend. This is a manual override — the next crawl will not undo it.'}
        confirmLabel={data.hidden ? 'Re-enable product' : 'Hide product'}
        onConfirm={moderate}
        onClose={() => setModerating(false)}
      />
    </div>
  );
}
